// FRU-24080 (ANGULAR-7EE): a step or answer post that gets no answer (status 0) on a page the visitor is still on was reported
// and lost: about 100 a day, mostly phones inside in-app browsers, where the request is dropped and Cloudflare logs a 499. Those
// posts are sent again once the page is visible and online, and only one that is still lost after the last try is reported.
import { EMPTY, NEVER, Observable, fromEvent, merge, of, race, timer } from 'rxjs';
import { filter, mergeMap, take } from 'rxjs/operators';

/** The pause before each try again while the page stays visible and online: 1 second, then 3. */
export const ACTION_RETRY_DELAYS_MS = [1000, 3000];
/** How long a try again waits for a hidden or offline page to come back. After that the post is given up quietly: the visitor
 *  has been away or offline for ten minutes, which is not a failure on our side. */
export const ACTION_RETRY_WAIT_CAP_MS = 10 * 60 * 1000;

export interface ActionRetryEnv {
  doc?: (EventTarget & { visibilityState?: string }) | null;
  win?: EventTarget | null;
  nav?: { onLine?: boolean } | null;
}

let testEnv: ActionRetryEnv | null = null;

/** Test-only: listen on fake page, window and network targets instead of the real ones. Null restores the real ones. */
export function setActionRetryEnvForTests(env: ActionRetryEnv | null): void {
  testEnv = env;
}

/** The page, window and network targets in use (the fake ones under test). FRU-24201's outbox listens on the same ones. */
export function actionRetryEnv(): Required<ActionRetryEnv> {
  return currentEnv();
}

function currentEnv(): Required<ActionRetryEnv> {
  const env = testEnv || {};
  return {
    doc: env.doc !== undefined ? env.doc : (typeof document !== 'undefined' ? document : null),
    win: env.win !== undefined ? env.win : (typeof window !== 'undefined' ? window : null),
    nav: env.nav !== undefined ? env.nav : (typeof navigator !== 'undefined' ? navigator : null),
  };
}

/**
 * When to send a post again after it got no answer: after a short pause (`pauseMs`, else 1 then 3 seconds by attempt), once the
 * page is visible and online. If the page goes first (pagehide), `onLeave` runs and the notifier completes without asking for
 * another try, and the same after ACTION_RETRY_WAIT_CAP_MS away.
 */
export function actionRetryNotifier(attempt: number, onLeave: () => void, pauseMs?: number): Observable<unknown> {
  const { doc, win, nav } = currentEnv();
  const hidden = () => { try { return !!doc && doc.visibilityState === 'hidden'; } catch (e) { return false; } };
  const offline = () => { try { return !!nav && nav.onLine === false; } catch (e) { return false; } };
  const ready = () => !hidden() && !offline();
  const leave$ = win
    ? fromEvent(win, 'pagehide').pipe(take(1), mergeMap(() => { try { onLeave(); } catch (e) { /* best effort */ } return EMPTY; }))
    : NEVER;
  // Waiting for the page to be visible and online again; after the cap it gives up quietly (completes without a try).
  const back$ = race(
    merge(doc ? fromEvent(doc, 'visibilitychange') : NEVER, win ? fromEvent(win, 'online') : NEVER).pipe(filter(ready), take(1)),
    timer(ACTION_RETRY_WAIT_CAP_MS).pipe(mergeMap(() => EMPTY)),
  );
  const pause = pauseMs !== undefined ? pauseMs : ACTION_RETRY_DELAYS_MS[Math.min(Math.max(attempt, 1), ACTION_RETRY_DELAYS_MS.length) - 1];
  // The pause always runs first (a server's Retry-After is a floor); then, if the page is hidden or offline, the try waits for
  // it to come back, so a try is never spent while the page can't send it.
  const due$ = timer(pause).pipe(mergeMap(() => (ready() ? of(0) : back$)));
  return race(due$, leave$);
}

/** Emits once when a page that went away is shown again (pageshow, as from the browser's back-forward cache). */
export function pageShownAgain(): Observable<unknown> {
  const { win } = currentEnv();
  return win ? fromEvent(win, 'pageshow').pipe(take(1)) : NEVER;
}
